/** Settings → Social presence: tenant-scoped links and handles without OAuth or availability claims. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useOrganization } from "@/auth/organization";
import { Button, ErrorAlert, Form, FullPageSpinner, Section, buttonClass, useToast } from "@/components/ui";
import { DraftOffer, DraftSavedNote } from "@/components/ui/FormDraft";
import { portal } from "@/lib/api";
import { useFormDraft } from "@/lib/form-drafts";
import type { SocialLinkStatus, SocialPlatform, SocialSettings, SocialSuggestion } from "@/lib/types";

import { ReadOnlyNote } from "./parts";

const PLATFORMS: { key: SocialPlatform; label: string; base: string; hosts: string[] }[] = [
  {
    key: "facebook",
    label: "Facebook",
    base: "https://www.facebook.com/",
    hosts: ["facebook.com", "www.facebook.com"],
  },
  {
    key: "instagram",
    label: "Instagram",
    base: "https://www.instagram.com/",
    hosts: ["instagram.com", "www.instagram.com"],
  },
  {
    key: "x",
    label: "X",
    base: "https://x.com/",
    hosts: ["x.com", "www.x.com", "twitter.com", "www.twitter.com"],
  },
];

const HANDLE_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

type Draft = Record<SocialPlatform, { url: string; handle: string }>;
type FieldErrors = Partial<Record<SocialPlatform, { url?: string; handle?: string }>>;

function draftFrom(saved: SocialSettings): Draft {
  return Object.fromEntries(
    PLATFORMS.map(({ key }) => {
      const link = saved.links.find((item) => item.platform === key);
      return [key, { url: link?.url ?? "", handle: link?.handle ?? "" }];
    }),
  ) as Draft;
}

/** The same checks the server applies, run locally so each field's error shows next to it immediately
 * (QB-049) instead of waiting on a round trip — the server still rejects anything this misses (AC2). */
function validate(platform: SocialPlatform, url: string, handle: string): FieldErrors[SocialPlatform] {
  const errors: { url?: string; handle?: string } = {};
  if (!HANDLE_RE.test(handle)) errors.handle = "Use a valid handle without spaces or an @ prefix";
  const hosts = PLATFORMS.find((p) => p.key === platform)?.hosts ?? [];
  try {
    const parsed = new URL(url);
    if (
      parsed.protocol !== "https:" ||
      !hosts.includes(parsed.hostname) ||
      !parsed.pathname.replace(/\/+$/, "")
    )
      errors.url = "Use the secure profile URL for the selected platform";
  } catch {
    errors.url = "Use the secure profile URL for the selected platform";
  }
  return Object.keys(errors).length ? errors : undefined;
}

const SUGGESTION_STATE: Record<SocialSuggestion["state"], { label: string; tone: string; next: string }> = {
  not_checked: {
    label: "Not checked",
    tone: "bg-muted text-fg-subtle",
    next: "Check availability directly on the platform before using it.",
  },
  available: { label: "Available", tone: "bg-[#d9f0e6] text-[#13734d]", next: "" },
  unavailable: {
    label: "Taken",
    tone: "bg-[#fbd6c9] text-[#b52522]",
    next: "Try a different suggestion or your own handle.",
  },
  unknown: {
    label: "Unknown",
    tone: "bg-[#fef4e5] text-[#853f00]",
    next: "We couldn't confirm this one — check it directly.",
  },
};

const STATUS_LABEL: Record<SocialLinkStatus, string> = {
  draft: "Not published",
  published: "Published",
  stale: "Changed since last publish",
  failed: "Publish failed",
};

export function SocialTab() {
  const { orgId, current, can } = useOrganization();
  const qc = useQueryClient();
  const queryKey = ["social", orgId];
  const saved = useQuery({
    queryKey,
    queryFn: () => portal.social(orgId, current.organization.name),
  });
  if (saved.isPending) return <FullPageSpinner />;
  if (saved.isError) return <ErrorAlert error={saved.error} />;
  return (
    <SocialForm
      key={orgId}
      saved={saved.data}
      editable={can("organization:update")}
      orgId={orgId}
      onSaved={(next) => qc.setQueryData(queryKey, next)}
    />
  );
}

function SocialForm({
  saved,
  editable,
  orgId,
  onSaved,
}: {
  saved: SocialSettings;
  editable: boolean;
  orgId: string;
  onSaved: (next: SocialSettings) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(saved));
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const kept = useFormDraft("settings.social", draft, { ready: editable });
  const links = useMemo(
    () =>
      Object.entries(draft)
        .filter(([, value]) => value.url.trim() || value.handle.trim())
        .map(([platform, value]) => ({ platform, url: value.url.trim(), handle: value.handle.trim() })),
    [draft],
  );
  const toast = useToast();
  const save = useMutation({
    mutationFn: () => portal.saveSocial(orgId, links),
    onSuccess: (next) => {
      kept.clear();
      setFieldErrors({});
      onSaved(next);
      toast.show({ tone: "success", title: "Social links saved" });
    },
  });
  const publish = useMutation({
    mutationFn: () => portal.publishSocial(orgId),
    onSuccess: (next) => {
      onSaved(next);
      toast.show({ tone: "success", title: "Social links published" });
    },
  });

  const failed = saved.links.filter((link) => link.status === "failed");
  const stale = saved.links.filter((link) => link.status === "stale");
  const publishDisabledReason =
    saved.links.length === 0 ? "Add and save at least one social link before publishing." : undefined;

  /** The Form's extra rules: each message shows on its field and, named by platform, in the summary. */
  function check() {
    const errors: FieldErrors = {};
    const byControl: Record<string, string | undefined> = {};
    for (const { platform, url, handle } of links) {
      const e = validate(platform as SocialPlatform, url, handle);
      if (!e) continue;
      errors[platform as SocialPlatform] = e;
      const label = PLATFORMS.find((p) => p.key === platform)?.label ?? platform;
      if (e.url) byControl[`${platform}.url`] = `${label}: ${e.url}`;
      if (e.handle) byControl[`${platform}.handle`] = `${label} handle: ${e.handle}`;
    }
    setFieldErrors(errors);
    return byControl;
  }

  return (
    <div className="flex w-full max-w-[760px] flex-col gap-4">
      <Section variant="tight">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-bold text-fg">Social presence</h2>
          <p className="text-[12.5px] leading-[1.55] text-fg-subtle">
            Add public profile links for your business. CortexGrip never signs in to social platforms or
            claims a handle is available until a platform checker exists.
          </p>
        </div>
        <Form
          className="flex flex-col gap-3.5"
          error={save.error}
          validate={check}
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <DraftOffer draft={kept} onContinue={setDraft} />
          {PLATFORMS.map(({ key, label, base }) => {
            const savedLink = saved.links.find((link) => link.platform === key);
            const errors = fieldErrors[key];
            const previewUrl = draft[key].url.trim();
            const previewHandle = draft[key].handle.trim();
            const hasPreview = previewUrl && previewHandle && !errors;
            return (
              <div key={key} className="flex flex-col gap-2 border-t border-border pt-4">
                <div className="grid gap-2 md:grid-cols-[120px_1fr_180px] md:items-start">
                  <label className="pt-2.5 text-[13px] font-semibold text-fg" htmlFor={`social-${key}-url`}>
                    {label}
                  </label>
                  <div className="flex flex-col gap-1.5">
                    <input
                      id={`social-${key}-url`}
                      name={`${key}.url`}
                      disabled={!editable}
                      placeholder={`${base}your-handle`}
                      value={draft[key].url}
                      aria-invalid={errors?.url ? true : undefined}
                      aria-describedby={errors?.url ? `social-${key}-url-error` : undefined}
                      onChange={(event) => {
                        setDraft((current) => ({
                          ...current,
                          [key]: { ...current[key], url: event.target.value },
                        }));
                        setFieldErrors((current) => ({ ...current, [key]: undefined }));
                      }}
                      className={`h-10 rounded-xl border bg-surface px-3 text-[13px] text-fg focus:border-brand-500 disabled:opacity-60 ${
                        errors?.url ? "border-red-500" : "border-border"
                      }`}
                    />
                    {errors?.url ? (
                      <p id={`social-${key}-url-error`} className="text-xs text-red-600">
                        {errors.url}
                      </p>
                    ) : (
                      <p className="text-[11.5px] text-fg-subtle">Secure profile URL</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <input
                      name={`${key}.handle`}
                      disabled={!editable}
                      aria-label={`${label} handle`}
                      aria-invalid={errors?.handle ? true : undefined}
                      aria-describedby={errors?.handle ? `social-${key}-handle-error` : undefined}
                      placeholder="handle"
                      value={draft[key].handle}
                      onChange={(event) => {
                        setDraft((current) => ({
                          ...current,
                          [key]: { ...current[key], handle: event.target.value },
                        }));
                        setFieldErrors((current) => ({ ...current, [key]: undefined }));
                      }}
                      className={`h-10 rounded-xl border bg-surface px-3 text-[13px] text-fg focus:border-brand-500 disabled:opacity-60 ${
                        errors?.handle ? "border-red-500" : "border-border"
                      }`}
                    />
                    {errors?.handle && (
                      <p id={`social-${key}-handle-error`} className="text-xs text-red-600">
                        {errors.handle}
                      </p>
                    )}
                  </div>
                </div>
                {hasPreview && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-[132px] w-fit truncate text-xs text-brand-500 underline-offset-2 hover:underline"
                  >
                    Preview: {previewUrl.replace(/^https?:\/\//, "")}
                  </a>
                )}
                {savedLink && (
                  <p className="ml-[132px] text-xs text-fg-subtle">
                    {STATUS_LABEL[savedLink.status]}
                    {savedLink.status === "failed" && savedLink.last_error
                      ? ` — ${savedLink.last_error}`
                      : ""}
                  </p>
                )}
              </div>
            );
          })}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button
              variant="brand"
              size="lg"
              type="submit"
              loading={save.isPending}
              requires="organization:update"
            >
              Save links
            </Button>
            <DraftSavedNote savedAt={kept.savedAt} />
            <Button
              size="lg"
              variant="secondary"
              loading={publish.isPending}
              requires="organization:update"
              disabledReason={publishDisabledReason}
              onClick={() => publish.mutate()}
            >
              Publish to live websites
            </Button>
            {editable && publishDisabledReason && (
              // Also the Publish button's description; shown for sighted users without hovering.
              <span aria-hidden className="text-xs text-fg-subtle">
                {publishDisabledReason}
              </span>
            )}
            <ReadOnlyNote permission="organization:update" />
            <ErrorAlert error={save.error ?? publish.error} />
          </div>
        </Form>
        {failed.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[#fff1ee] px-3.5 py-3">
            <p className="min-w-0 flex-1 text-[12.5px] text-[#b52522]">
              Publishing failed for {failed.map((link) => link.platform).join(", ")}. Your last confirmed
              version is still live.
            </p>
            <button
              type="button"
              onClick={() => publish.mutate()}
              disabled={publish.isPending}
              aria-busy={publish.isPending}
              className={buttonClass("secondary", "pill")}
            >
              {publish.isPending ? "Retrying…" : "Retry publish"}
            </button>
          </div>
        )}
        {failed.length === 0 && stale.length > 0 && (
          <div className="rounded-xl bg-[#fef4e5] px-3.5 py-3 text-[12.5px] text-[#853f00]">
            You have unpublished changes to {stale.map((link) => link.platform).join(", ")}.
          </div>
        )}
      </Section>

      <Section variant="tight">
        <h2 className="text-[15px] font-bold text-fg">Handle suggestions</h2>
        <p className="text-[12.5px] text-fg-subtle">
          Suggestions are deterministic candidates only. Check availability directly with each platform.
        </p>
        <div className="flex flex-wrap gap-2">
          {saved.suggestions.map((suggestion) => {
            const state = SUGGESTION_STATE[suggestion.state];
            return (
              <div key={`${suggestion.platform}-${suggestion.handle}`} className="flex flex-col gap-1">
                <Button
                  unstyled
                  requires="organization:update"
                  onClick={() =>
                    setDraft((current) => ({
                      ...current,
                      [suggestion.platform]: {
                        handle: suggestion.handle,
                        url: `${PLATFORMS.find((item) => item.key === suggestion.platform)?.base}${suggestion.handle}`,
                      },
                    }))
                  }
                  className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-fg hover:border-brand-500 disabled:opacity-50"
                >
                  {suggestion.platform}: @{suggestion.handle}
                  <span className={`rounded-full px-1.5 py-0.5 text-[10.5px] font-semibold ${state.tone}`}>
                    {state.label}
                  </span>
                </Button>
                {state.next && <p className="pl-1 text-[11px] text-fg-subtle">{state.next}</p>}
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
